import Icon from '@ant-design/icons';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import RightSvg from '@/components/Icon/svgs/right.svg';
import type { INoteAttachment } from '@/service/note';

import styles from './index.less';

interface IProps {
  className?: string;
  list: INoteAttachment[];
  mouseEnter?: boolean;
}

const ScrollCard = (props: IProps) => {
  const { className, list, mouseEnter } = props;
  const [showLeft, setShowLeft] = useState<boolean>(false);
  const [showRight, setShowRight] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  // 更新按钮disabled状态
  const checkScrollDirection = () => {
    const container = containerRef.current;
    const deviation = 2;

    if (container) {
      setShowLeft(container.scrollLeft > deviation);

      const remainingScrollWidth =
        container.scrollWidth - container.clientWidth - container.scrollLeft;

      setShowRight(remainingScrollWidth > deviation);
    }
  };

  // debounce
  const debouncedCheckScrollDirection = () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(checkScrollDirection, 100);
  };

  // 滚动的时候有过渡动画效果
  const smoothScroll = (container: HTMLDivElement, targetScroll: number, speed: number) => {
    const startScroll = container.scrollLeft;
    let startTime: number | null = null;

    const draw = (currentTime: number) => {
      if (!startTime) {
        startTime = currentTime;
      }

      const progress = (currentTime - startTime) / speed;
      const scrollLeft = startScroll + (targetScroll - startScroll) * progress;

      if (progress < 1) {
        container.scrollLeft = scrollLeft;
        window.requestAnimationFrame(draw);
      } else {
        container.scrollLeft = targetScroll;
      }
    };

    window.requestAnimationFrame(draw);
  };

  const scroll = (direction: 'left' | 'right') => {
    const container = containerRef.current;

    if (container) {
      // 每次滚动的距离,获取父容器动态计算的宽度

      const scrollAmount = container.clientWidth - 35;

      const remainingScrollWidth =
        container.scrollWidth - container.clientWidth - container.scrollLeft;

      const canScrollAmount =
        direction === 'left'
          ? Math.min(scrollAmount, container.scrollLeft)
          : Math.min(scrollAmount, remainingScrollWidth);

      const targetScroll =
        container.scrollLeft + (direction === 'left' ? -canScrollAmount : canScrollAmount);

      smoothScroll(container, targetScroll, 200); // 设置滚动速度
    }
  };

  // 为什么不写成useEffect？ 因为effect的时候子元素还没完全挂载全，导致scrollWidth不准确
  // 但是div没有onLoad，加一个隐藏的img元素，监听onLoad事件
  const handleOnLoad = () => {
    const container = containerRef.current;

    if (container) {
      checkScrollDirection();

      container.addEventListener('scroll', debouncedCheckScrollDirection);
    }
  };

  useEffect(() => {
    const container = containerRef.current;

    return () => {
      if (container) {
        container.removeEventListener('scroll', debouncedCheckScrollDirection);
      }
    };
  }, []);

  return (
    <div className={classNames('flex flex-col', !list.length ? 'hidden' : '', className)}>
      <div className="relative w-full">
        {showLeft && mouseEnter && (
          <Icon
            className={classNames(styles.scrollButton, styles.left, {
              [styles.canClick]: showLeft
            })}
            component={RightSvg}
            onClick={(e) => {
              e.stopPropagation();
              scroll('left');
            }}
          />
        )}
        {showRight && mouseEnter && (
          <Icon
            className={classNames(styles.scrollButton, styles.right, {
              [styles.canClick]: showRight
            })}
            component={RightSvg}
            onClick={(e) => {
              e.stopPropagation();
              scroll('right');
            }}
          />
        )}
        <div ref={containerRef} className={styles.scrollCardContainer} onLoad={handleOnLoad}>
          {list.map((item) => (
            <img
              key={item.id}
              className="min-w-[180px] h-full object-cover rounded-xl"
              src={item.url}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScrollCard;
